// eslint-disable-next-line no-restricted-imports
import { useMutation, useQuery } from 'react-query';
import { AxiosResponse } from 'axios';
import { Meta } from 'types/shared';

import { API } from 'core/api';
import { useCognitoUser } from 'core/cognito';
import { createNamedBlob, NamedBlob } from 'core/shared';
import { useToast } from 'core/toast/hooks';

interface ReceiptStatistics {
  COMPLETED?: number;
  PENDING?: number;
  SKIPPED?: number;
  PUBLIC_TRANSPORT?: number;
}

interface UseReceiptStatisticsProps {
  organisationId?: string;
  startPeriod?: string;
  endPeriod?: string;
}

function useReceiptStatistics({
  organisationId,
  startPeriod,
  endPeriod,
}: UseReceiptStatisticsProps) {
  const { token } = useCognitoUser();
  const { addToast } = useToast();
  interface Response {
    result: ReceiptStatistics;
    meta: Meta;
  }

  interface Params {
    organisation_id?: string;
    start_period: string;
    end_period: string;
  }

  function fetchData() {
    return API.get<Response, Params, false>({
      path: '/admin/statistics/receipts',
      token,
      params: {
        organisation_id: organisationId,
        start_period: startPeriod ?? '',
        end_period: endPeriod ?? '',
      },
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });
  }

  return useQuery<AxiosResponse<Response>, unknown, Response>(
    ['receiptStatistics', { organisationId, startPeriod, endPeriod }],
    fetchData,
    {
      staleTime: Number.MAX_VALUE,
      enabled: !!token && !!startPeriod && !!endPeriod,
      retry: 5,
      select: ({ data }) => data,
      keepPreviousData: true,
    },
  );
}

interface UserStatistics {
  [key: string]: { consumers: number; employees: number };
}

function useUserStatistics() {
  const { token } = useCognitoUser();
  const { addToast } = useToast();
  interface Response {
    result: UserStatistics;
    meta: Meta;
  }

  function fetchData() {
    return API.get<Response, undefined, false>({
      path: '/admin/statistics/users',
      token,
      params: undefined,
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });
  }

  return useQuery<AxiosResponse<Response>, unknown, Response>(['consumerStatistics'], fetchData, {
    staleTime: Number.MAX_VALUE,
    enabled: !!token,
    retry: 5,
    select: ({ data }) => data,
    keepPreviousData: true,
  });
}

type SpendCategory =
  | 'CHECK'
  | 'VECORE'
  | 'FELYX'
  | 'MYWHEELS'
  | 'MYWHEELS Commission'
  | 'GREENWHEELS'
  | 'GREENWHEELS Commission'
  | 'GREENWHEELS_RNP'
  | 'GREENWHEELS_RNP Commission'
  | 'ADYEN'
  | 'NS'
  | 'REVENUE' // Legacy subscription
  | 'SUBSCRIPTION'
  | 'USAGE_FEE';

type BusinessSpendCategory = SpendCategory | 'ONETIMEFEE' | 'CREDIT' | 'PASSTHROUGH' | 'OTHER';

interface MonthData<Category extends string = SpendCategory> {
  by_month: Record<string, Record<Category, number>>[];
  compound_growth_rate: number;
  // Dropped from the business and cashflow responses; the reservation ones still carry it.
  forecasted?: number;
  monthly_growth: number;
  total: number;
}

interface ReservationStatistics {
  top_days: { day: string; amount: number }[];
  bottom_days: { day: string; amount: number }[];
  completed: MonthData;
  cancelled: MonthData;
  spend: MonthData;
  reservation_types: {
    Business: number;
    Consumer: number;
    Private: number;
  };
}

function useReservationStatistics() {
  const { token } = useCognitoUser();
  const { addToast } = useToast();
  function fetchData() {
    return API.get<ReservationStatistics, undefined, false>({
      path: '/admin/statistics/reservations',
      token,
      params: undefined,
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });
  }

  return useQuery<AxiosResponse<ReservationStatistics>, unknown, ReservationStatistics>(
    ['reservationStatistics'],
    fetchData,
    {
      staleTime: Number.MAX_VALUE,
      enabled: !!token,
      retry: 5,
      select: ({ data }) => data,
      keepPreviousData: true,
    },
  );
}

// Both VAT variants come back in one response, so the tab can toggle between
// them without another request.
interface BusinessStatistics {
  spend: MonthData<BusinessSpendCategory>;
  spend_net: MonthData<BusinessSpendCategory>;
}

type CashflowCategory = Extract<
  BusinessSpendCategory,
  | 'SUBSCRIPTION'
  | 'USAGE_FEE'
  | 'ONETIMEFEE'
  | 'MYWHEELS Commission'
  | 'GREENWHEELS Commission'
  | 'GREENWHEELS_RNP Commission'
>;

interface CashflowStatistics {
  spend: MonthData<CashflowCategory>;
  spend_net: MonthData<CashflowCategory>;
}

function useCashflowStatistics() {
  const { token } = useCognitoUser();
  const { addToast } = useToast();
  function fetchData() {
    return API.get<CashflowStatistics, undefined, false>({
      path: '/admin/statistics/cashflow',
      token,
      params: undefined,
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });
  }

  return useQuery<AxiosResponse<CashflowStatistics>, unknown, CashflowStatistics>(
    ['cashflowStatistics'],
    fetchData,
    {
      staleTime: Number.MAX_VALUE,
      enabled: !!token,
      retry: 5,
      select: ({ data }) => data,
      keepPreviousData: true,
    },
  );
}

function useBusinessStatistics() {
  const { token } = useCognitoUser();
  const { addToast } = useToast();
  function fetchData() {
    return API.get<BusinessStatistics, undefined, false>({
      path: '/admin/statistics/business',
      token,
      params: undefined,
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });
  }

  return useQuery<AxiosResponse<BusinessStatistics>, unknown, BusinessStatistics>(
    ['businessStatistics'],
    fetchData,
    {
      staleTime: Number.MAX_VALUE,
      enabled: !!token,
      retry: 5,
      select: ({ data }) => data,
      keepPreviousData: true,
    },
  );
}

interface ProviderStatistics {
  result: {
    [key: string]: {
      converted: number;
      pending: number;
    };
  };
  meta: Meta;
}

function useProviderStatistics() {
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  function fetchData() {
    return API.get<ProviderStatistics, undefined, false>({
      path: '/admin/statistics/providers',
      token,
      params: undefined,
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });
  }

  return useQuery<AxiosResponse<ProviderStatistics>, unknown, ProviderStatistics>(
    ['providerStatistics'],
    fetchData,
    {
      staleTime: Number.MAX_VALUE,
      enabled: !!token,
      retry: 5,
      select: ({ data }) => data,
      keepPreviousData: true,
    },
  );
}

// ---------------------------------------------------------------------------
// MRR: recurring revenue per month, spread out over the periods of the
// subscription invoice lines that produce it.
// ---------------------------------------------------------------------------

interface MrrContributionLine {
  invoice_line_id: string;
  invoice_id: string;
  invoice_reference: string;
  product_name: string;
  description: string;
  // Period the invoice line covers; the line's total is spread over `months`.
  period_start: string;
  period_end: string;
  months: number;
  // This month's share, gross (incl VAT) and net (excl VAT), in cents.
  amount: number;
  amount_net: number;
  // The whole invoice line, in cents.
  total_amount: number;
  total_amount_net: number;
}

// One customer's share of a month: every line invoiced to it added up, so a
// discount is subtracted from the subscription it was invoiced next to, and an
// organisation with more than one invoice is still a single row.
interface MrrContribution {
  organisation_id: string | null;
  customer_name: string;
  description: string;
  // Only filled when every line covers the same period, null otherwise.
  period_start: string | null;
  period_end: string | null;
  // This month's share, gross (incl VAT) and net (excl VAT), in cents.
  amount: number;
  amount_net: number;
  // Left out entirely when the backend has no lines to attach to the share.
  lines?: MrrContributionLine[];
}

interface MrrMonth {
  // First day of the month, ISO YYYY-MM-DD.
  month: string;
  mrr: number; // incl VAT, cents
  mrr_net: number; // excl VAT, cents
  customer_count: number;
  contributions: MrrContribution[];
}

interface MrrStatistics {
  result: {
    start: string;
    end: string;
    by_month: MrrMonth[];
  };
  meta: Meta;
}

interface UseMrrStatisticsProps {
  months: number;
}

function useMrrStatistics({ months }: UseMrrStatisticsProps) {
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  interface Params {
    months: number;
  }

  function fetchData() {
    return API.get<MrrStatistics, Params, false>({
      path: '/admin/statistics/mrr',
      token,
      params: { months },
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });
  }

  return useQuery<AxiosResponse<MrrStatistics>, unknown, MrrStatistics>(
    ['mrrStatistics', { months }],
    fetchData,
    {
      staleTime: Number.MAX_VALUE,
      enabled: !!token,
      retry: 1,
      select: ({ data }) => data,
      keepPreviousData: true,
    },
  );
}

// ---------------------------------------------------------------------------
// MRR forecast: the same monthly spread, projected forward from the billing
// contracts instead of read from the invoices.
// ---------------------------------------------------------------------------

interface MrrForecastContributionLine {
  description: string;
  // A recurring discount is invoiced next to the subscription and lowers it.
  category: 'subscription' | 'discount';
  // Month the line is going to be invoiced, ISO YYYY-MM-DD.
  invoice_date: string;
  period_start: string;
  period_end: string;
  months: number;
  // This month's share, gross (incl VAT) and net (excl VAT), in cents.
  amount: number;
  amount_net: number;
  total_amount: number;
  total_amount_net: number;
}

// One contract's share of a month: every line it is going to be invoiced added
// up, so the discount is netted off the subscription, exactly like the
// realised report groups a customer's invoice lines.
interface MrrForecastContribution {
  organisation_id: string;
  customer_name: string;
  description: string;
  // Only filled when every line covers the same period, null otherwise.
  period_start: string | null;
  period_end: string | null;
  // This month's share, gross (incl VAT) and net (excl VAT), in cents.
  amount: number;
  amount_net: number;
  // Last day of a fixed contract term, null for open-ended contracts.
  contract_end: string | null;
  // Left out entirely when the backend has no lines to attach to the share.
  lines?: MrrForecastContributionLine[];
}

interface MrrForecastMonth {
  month: string;
  mrr: number;
  mrr_net: number;
  customer_count: number;
  contributions: MrrForecastContribution[];
}

interface MrrForecast {
  result: {
    start: string;
    end: string;
    by_month: MrrForecastMonth[];
  };
  meta: Meta;
}

function useMrrForecast({ months }: UseMrrStatisticsProps) {
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  interface Params {
    months: number;
  }

  function fetchData() {
    return API.get<MrrForecast, Params, false>({
      path: '/admin/statistics/mrr/forecast',
      token,
      params: { months },
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });
  }

  return useQuery<AxiosResponse<MrrForecast>, unknown, MrrForecast>(
    ['mrrForecast', { months }],
    fetchData,
    {
      staleTime: Number.MAX_VALUE,
      enabled: !!token,
      retry: 1,
      select: ({ data }) => data,
      keepPreviousData: true,
    },
  );
}

interface MrrStatisticsCSVParams {
  // First day of the month to export, ISO YYYY-MM-DD.
  month: string;
}

function useMrrStatisticsCSV() {
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  async function mrrStatisticsCSV({ month }: MrrStatisticsCSVParams) {
    const { data, headers } = await API.get<Blob, MrrStatisticsCSVParams, true>({
      path: '/admin/statistics/mrr/export',
      token,
      version: 1,
      params: { month },
      expectFile: true,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });

    return Promise.resolve(createNamedBlob({ blob: data, headers }));
  }

  return useMutation<NamedBlob, unknown, MrrStatisticsCSVParams>((data: MrrStatisticsCSVParams) =>
    mrrStatisticsCSV(data),
  );
}

interface OrganisationStatisticsParams {
  month: string;
}

function useOrganisationStatistics({ month }: OrganisationStatisticsParams) {
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  interface Params {
    month: string;
    format: 'default';
  }

  interface OrganisationStatisticsResults {
    result: Record<
      string,
      {
        total_spend: number;
        providers: Record<string, number>;
        revenue: number;
        active_users: number;
        total_users: number;
      }
    >;
    meta: Meta;
  }

  function fetchData() {
    return API.get<OrganisationStatisticsResults, Params, false>({
      path: '/admin/statistics/organisations',
      token,
      params: { month, format: 'default' },
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });
  }

  return useQuery<
    AxiosResponse<OrganisationStatisticsResults>,
    unknown,
    OrganisationStatisticsResults
  >(['organisationStatistics', { month }], fetchData, {
    staleTime: Number.MAX_VALUE,
    enabled: !!token,
    retry: 1,
    select: ({ data }) => data,
    keepPreviousData: false,
  });
}

function useOrganisationStatisticsCSV() {
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  interface Params {
    month: string;
    format: 'csv';
  }

  async function organisationStatisticsCSV({ month }: OrganisationStatisticsParams) {
    const { data, headers } = await API.get<Blob, Params, true>({
      path: `/admin/statistics/organisations`,
      token,
      version: 1,
      params: { month, format: 'csv' },
      expectFile: true,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });

    return Promise.resolve(createNamedBlob({ blob: data, headers }));
  }

  return useMutation<NamedBlob, unknown, OrganisationStatisticsParams>(
    (data: OrganisationStatisticsParams) => organisationStatisticsCSV(data),
  );
}

export {
  type BusinessSpendCategory,
  type CashflowCategory,
  type MrrContribution,
  type MrrContributionLine,
  type MrrForecast,
  type MrrForecastMonth,
  type MrrMonth,
  type MrrStatistics,
  useBusinessStatistics,
  useCashflowStatistics,
  useMrrForecast,
  useMrrStatistics,
  useMrrStatisticsCSV,
  useOrganisationStatistics,
  useOrganisationStatisticsCSV,
  useProviderStatistics,
  useReceiptStatistics,
  useReservationStatistics,
  useUserStatistics,
};
